import React, { useMemo } from "react";
import { useAppSelector, useAppDispatch } from "@/store/index";
import {
  SocialChannel,
  AdminSocialPreset,
  AllAdminPresets,
  ChannelConfigs,
} from "@/database";
import { getAdminPresetList } from "@/store/media/media.action";
import { getPlatformIcon } from "@/utils/utilFunctions/commmonFunctions";
import PickerSheet, { RailEntry } from "../PickerSheet";

/** Left-rail keys are namespaced so a platform never collides with a preset id. */
const platformKey = (p: string) => `pf:${p}`;
const presetKey = (id: string) => `ps:${id}`;

/** Stable rail order for the common platforms; anything else follows alphabetically. */
const PLATFORM_ORDER = [
  "youtube",
  "facebook",
  "instagram",
  "linkedin",
  "twitter",
  "dailymotion",
];

const platformLabel = (p: string) =>
  p === "youtube"
    ? "YouTube"
    : p === "linkedin"
      ? "LinkedIn"
      : p.charAt(0).toUpperCase() + p.slice(1);

const PlatformIcon: React.FC<{ platform: string; size?: number }> = ({
  platform,
  size = 16,
}) => {
  const { icon, color } = getPlatformIcon(platform || "");
  return typeof icon === "string" ? (
    <i className={`bi ${icon}`} style={{ color, fontSize: size }} />
  ) : (
    <span className="d-inline-flex">{icon}</span>
  );
};

export type PickItem = {
  itemId: string;
  channelId: string;
  channel: SocialChannel;
  kind: "plain" | "preset";
  presetId?: string;
  presetName?: string;
  config?: ChannelConfigs;
};

export type ExpandDescriptor = {
  channelId: string;
  channel: SocialChannel;
  kind: "plain" | "preset";
  presetId?: string;
  presetName?: string;
  config?: ChannelConfigs;
};

export const plainItemId = (channelId: string) => `plain:${channelId}`;
export const presetItemId = (presetId: string, channelId: string) =>
  `preset:${presetId}:${channelId}`;

const SocialExpandSheet: React.FC<{
  show: boolean;
  selected: Set<string>;
  onClose: () => void;
  onApply: (next: ExpandDescriptor[]) => void;
}> = ({ show, selected, onClose, onApply }) => {
  const dispatch = useAppDispatch();

  const socialChannelsList = useAppSelector(
    (s) => s.app.userSocialChannels.socialChannelsList
  ) as SocialChannel[] | undefined;
  const adminPresets = useAppSelector(
    (s) => s.media.adminPresetList.list
  ) as AllAdminPresets;
  const activeChannels = useMemo(
    () => (socialChannelsList || []).filter((c) => c.status === 1),
    [socialChannelsList]
  );

  // Platforms present in the connected-channel list, in rail order.
  const platforms = useMemo(() => {
    const seen = new Set(
      activeChannels.map((c) => (c.platform || "").toLowerCase())
    );
    seen.delete("");
    const ordered = PLATFORM_ORDER.filter((p) => seen.has(p));
    const rest = [...seen].filter((p) => !PLATFORM_ORDER.includes(p)).sort();
    return [...ordered, ...rest];
  }, [activeChannels]);

  // Admin publish presets (the same list the publish page's PRESET dropdown shows).
  const socialPresets = useMemo(
    () => adminPresets?.publish_preset || [],
    [adminPresets]
  );

  const presetPairs = (
    preset: AdminSocialPreset
  ): { channel: SocialChannel; config: ChannelConfigs }[] => {
    const channels = activeChannels;
    return (preset.channel_configs || [])
      .map((cfg) => {
        const channel = channels.find(
          (c) =>
            (c.name === cfg.channel_name &&
              (c.platform === cfg.job_type || c.platform === cfg.platform)) ||
            c.id === cfg.channel_id ||
            c.channel === cfg.channel_id
        );
        return channel ? { channel, config: cfg } : null;
      })
      .filter(
        (x): x is { channel: SocialChannel; config: ChannelConfigs } => !!x
      );
  };

  const rail = useMemo<RailEntry<PickItem>[]>(
    () => [
      ...platforms.map((p) => ({
        key: platformKey(p),
        label: platformLabel(p),
        icon: <PlatformIcon platform={p} />,
        items: activeChannels
          .filter((c) => (c.platform || "").toLowerCase() === p)
          .map<PickItem>((c) => ({
            itemId: plainItemId(c.id),
            channelId: c.id,
            channel: c,
            kind: "plain",
          })),
        section: "Social",
      })),
      ...socialPresets.map((preset) => {
        const pid = preset.id || preset.name || "";
        return {
          key: presetKey(pid),
          label: preset.name || "Untitled preset",
          icon: (
            <i
              className="bi bi-stack text-secondary"
              style={{ fontSize: 14 }}
            />
          ),
          items: presetPairs(preset).map<PickItem>(({ channel, config }) => ({
            itemId: presetItemId(pid, channel.id),
            channelId: channel.id,
            channel,
            kind: "preset",
            presetId: pid,
            presetName: preset.name,
            config,
          })),
          section: "Publishing Presets",
          // Preset click only opens its destinations (nothing pre-staged); the pane's
          // "Apply All" CTA stages them all at once.
          stageAllCta: "Add All",
        };
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [platforms, activeChannels, socialPresets]
  );

  const byItemId = useMemo(() => {
    const m = new Map<string, PickItem>();
    rail.forEach((entry) => entry.items.forEach((it) => m.set(it.itemId, it)));
    return m;
  }, [rail]);

  const presetOwner = useMemo(() => {
    const m = new Map<string, string>();
    selected.forEach((itemId) => {
      const it = byItemId.get(itemId);
      if (it?.kind === "preset" && it.presetId)
        m.set(it.channelId, it.presetId);
    });
    return m;
  }, [selected, byItemId]);

  const isItemDisabled = (it: PickItem): boolean => {
    const owner = presetOwner.get(it.channelId);
    if (!owner) return false;
    return !(it.kind === "preset" && it.presetId === owner);
  };

  const computeNext = (
    itemId: string,
    item: PickItem,
    current: Set<string>
  ): Set<string> => {
    const next = new Set(current);
    if (next.has(itemId)) {
      next.delete(itemId);
      return next;
    }
    [...next].forEach((otherId) => {
      const other = byItemId.get(otherId);
      if (other && other.channelId === item.channelId) next.delete(otherId);
    });
    next.add(itemId);
    return next;
  };

  const handleApply = (next: Set<string>) => {
    const descriptors: ExpandDescriptor[] = [];
    next.forEach((itemId) => {
      const it = byItemId.get(itemId);
      if (!it) return;
      descriptors.push({
        channelId: it.channelId,
        channel: it.channel,
        kind: it.kind,
        presetId: it.presetId,
        presetName: it.presetName,
        config: it.config,
      });
    });
    onApply(descriptors);
  };

  return (
    <PickerSheet<PickItem>
      show={show}
      selected={selected}
      onClose={onClose}
      onApply={handleApply}
      computeNext={computeNext}
      isItemDisabled={isItemDisabled}
      disabledHint="Already added from a preset — remove it there first to select it here."
      rail={rail}
      defaultActiveKey={
        platforms.length > 0 ? platformKey(platforms[0]) : undefined
      }
      paneTitle="Destinations"
      // Selected-column chips carry the platform icon, matching the inline publish chips.
      renderChipLeading={(it) => (
        <PlatformIcon platform={it.channel.platform || ""} size={13} />
      )}
      searchPlaceholder="Search channels"
      emptySearchText="No channels match your search."
      emptyText="No channels here."
      getId={(it) => it.itemId}
      getSearchText={(it) => it.channel.name || ""}
      onOpen={() => {
        if (!adminPresets?.publish_preset?.length)
          dispatch(
            getAdminPresetList({
              keys: ["publish_preset"],
              status: "active",
            }) as any
          );
      }}
      renderItemLeading={(it) =>
        it.channel.image ? (
          <img
            src={it.channel.image}
            alt=""
            className="rounded-circle flex-shrink-0"
            style={{ width: 20, height: 20, objectFit: "cover" }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <PlatformIcon platform={it.channel.platform} size={14} />
        )
      }
      renderItemBody={(it) => (
        <>
          <div className="text-truncate">{it.channel.name}</div>
          <div className="text-muted text-truncate" style={{ fontSize: 11 }}>
            {platformLabel((it.channel.platform || "").toLowerCase())}
          </div>
        </>
      )}
    />
  );
};

export default SocialExpandSheet;
